import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faSync } from '@fortawesome/free-solid-svg-icons';
import { useNotifications } from './NotificationContext.js';
import './ConnectionStatusBanner.css';

/**
 * States in which this tab is no longer receiving updates. `disconnected` is deliberately absent:
 * it is also the state before login and after logout, where a warning would be wrong.
 */
const UNHEALTHY_STATES = new Set(['connecting', 'error', 'degraded', 'failed']);

/**
 * A redeploy or a brief network blip resolves well inside this window, and interrupting everyone
 * for those would train them to ignore the banner.
 */
const VISIBLE_AFTER_MS = 10000;

/**
 * Tells the user when their own actions still work but they have stopped seeing other people's.
 * Without it, a dead connection is indistinguishable from a quiet clinic.
 */
const ConnectionStatusBanner = () => {
  const { manager, addConnectionStateCallback, getConnectionState } = useNotifications();
  const [connectionState, setConnectionState] = useState('disconnected');
  const [isVisible, setIsVisible] = useState(false);
  // Held across unhealthy transitions: the retry loop cycles error -> connecting -> error, so a
  // timer restarted per state would never elapse.
  const unhealthySinceRef = useRef(null);

  // Keyed on the manager because this component renders alongside the provider: child effects
  // run first, so on the initial pass there is nothing to subscribe to yet.
  useEffect(() => {
    if (!manager) return;
    setConnectionState(getConnectionState());
    return addConnectionStateCallback(setConnectionState);
  }, [manager, addConnectionStateCallback, getConnectionState]);

  useEffect(() => {
    if (!UNHEALTHY_STATES.has(connectionState)) {
      unhealthySinceRef.current = null;
      setIsVisible(false);
      return;
    }

    if (unhealthySinceRef.current == null) {
      unhealthySinceRef.current = Date.now();
    }

    const remaining = VISIBLE_AFTER_MS - (Date.now() - unhealthySinceRef.current);
    if (remaining <= 0) {
      setIsVisible(true);
      return;
    }

    const timer = setTimeout(() => setIsVisible(true), remaining);
    return () => clearTimeout(timer);
  }, [connectionState]);

  if (!isVisible) {
    return null;
  }

  // `failed` is only reached when the server declined the connection outright, which retrying
  // cannot fix; everything else is still being retried in the background.
  const isTerminal = connectionState === 'failed';

  return (
    <div className="connection-status-banner" role="status">
      <FontAwesomeIcon icon={isTerminal ? faExclamationTriangle : faSync} spin={!isTerminal} />
      <span className="connection-status-banner__message">
        {isTerminal
          ? 'Live updates have stopped. Reload the page to start receiving them again.'
          : 'Reconnecting. You may not be seeing changes made by other people.'}
      </span>
      <button
        type="button"
        className="connection-status-banner__action"
        onClick={() => window.location.reload()}
      >
        Reload
      </button>
    </div>
  );
};

export default ConnectionStatusBanner;
